import React, { useState } from "react";
import { FaArrowRight } from "react-icons/fa";
import easyOnboardingImage from "../../assets/easy-onboarding.png";
import instantJobAlertsImage from "../../assets/job-alerts.png";
import placeholderImage from "../../assets/comm-manager-placeholder.png";
import measureImpactImage from "../../assets/measure-impact.png";
import employerMatchingImage from "../../assets/employer-matching.png";

interface Service {
  id: string;
  title: string;
  description: string;
}

interface CreateImpactWithCommunityProps {
  className?: string;
  ctaHref?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
}

const services: Service[] = [
  {
    id: "easy-onboarding",
    title: "Easy onboarding",
    description:
      "Onboard members in minutes, tag by skill or location, and assign verified LaborPass profiles.",
  },
  {
    id: "instant-job-alerts",
    title: "Instant job alerts",
    description:
      "Share verified job opportunities via WhatsApp, SMS, or email and get them hired even faster.",
  },
  {
    id: "employer-matching",
    title: "Employer matching",
    description:
      "Match members to active jobs based on skills availability, and experience.",
  },
  {
    id: "measure-impact",
    title: "Measure impact",
    description:
      "Track outcomes - who gets hired, what they earn, and how they grow with full automated reporting.",
  },
];

export const CreateImpactWithCommunity: React.FC<CreateImpactWithCommunityProps> =
  ({ className = "", ctaHref, ctaLabel = "Get Started", onCtaClick }) => {
    const [activeService, setActiveService] = useState("easy-onboarding");

    const getBackgroundImage = () => {
      switch (activeService) {
        case "easy-onboarding":
          return easyOnboardingImage;
        case "instant-job-alerts":
          return instantJobAlertsImage;
        case "measure-impact":
          return measureImpactImage;
        case "employer-matching":
          return employerMatchingImage;
        default:
          return placeholderImage;
      }
    };

    return (
      <div className={`w-full py-12 md:py-16 ${className}`}>
        <div className="relative">
          <div className="relative" style={{ border: "0.5px solid #121212" }}>
            {/* Top Section - Main Header */}
            <div className="py-10 md:py-16 px-6 md:px-10">
              <div className="flex flex-col gap-4">
                <h2 className="text-xl md:text-[40px] font-medium text-[#040A0D] leading-[1.2]">
                  Create impact with your community
                </h2>
              </div>
            </div>

            {/* Bottom Section with Services and Image */}
            <div
              className="relative grid grid-cols-1 lg:grid-cols-2"
              style={{ borderTop: "0.5px solid #121212" }}
            >
              {/* Left Side - Services List */}
              <div className="relative py-10 px-7 md:px-[45px] overflow-hidden">
                {/* Continuous background border */}
                <div className="absolute left-6 md:left-12 top-10 bottom-10 border-l-[2.5px] border-[#D0D6D9]"></div>

                {/* Services */}
                <div className="max-w-[430px] ">
                  {services.map((service, index) => {
                    const isActive = activeService === service.id;
                    return (
                      <div
                        key={service.id}
                        className={`relative flex flex-col gap-2 cursor-pointer transition-colors duration-200 ${
                          index > 0 ? "mt-12 md:mt-16" : ""
                        }`}
                        onClick={() => setActiveService(service.id)}
                        style={{
                          borderLeft: isActive
                            ? "2.5px solid #153243"
                            : "2.5px solid transparent",
                          marginLeft: "-2.5px",
                          paddingLeft: "16px",
                        }}
                      >
                        <h3
                          className={`text-xl md:text-[26px] font-medium leading-[1.23] font-[AeonikPro] ${
                            isActive
                              ? "text-[#212121]"
                              : "text-[#828282] hover:text-[#464646]"
                          }`}
                        >
                          {service.title}
                        </h3>
                        <p
                          className={`text-base md:text-lg leading-[1.67] font-[Inter] ${
                            isActive
                              ? "text-[#464646]"
                              : "text-[#828282] hover:text-[#646464]"
                          }`}
                        >
                          {service.description}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Right Side - Box with Image (top) and Button (bottom), centered */}
              <div className="relative flex items-center justify-center p-6">
                <div
                  className="p-2 w-full max-w-[480px] flex flex-col gap-4"
                  style={{ border: "0.5px solid #121212" }}
                >
                  {/* Image Area */}
                  <div
                    className="relative w-full h-[400px] lg:h-[480px] aspect-[16/9] bg-contain bg-center transition-all duration-500"
                    style={{
                      backgroundImage: `url(${getBackgroundImage()})`,
                    }}
                  ></div>

                  {/* Button */}
                  {onCtaClick ? (
                    <button
                      type="button"
                      onClick={onCtaClick}
                      className="w-full bg-[#ED6E5C] hover:bg-[#ED6E5C]/80 text-[#FEFEFE] transition-colors duration-200 flex items-center justify-center gap-2 font-[Inter] text-lg font-medium leading-[1.333]"
                      style={{ height: "60px" }}
                    >
                      {ctaLabel}
                      <FaArrowRight className="w-3 lg:w-4 h-3 lg:h-4" />
                    </button>
                  ) : (
                    <a
                      href={ctaHref}
                      className="w-full bg-[#ED6E5C] hover:bg-[#ED6E5C]/80 text-[#FEFEFE] transition-colors duration-200 flex items-center justify-center gap-2 font-[Inter] text-lg font-medium leading-[1.333]"
                      style={{ height: "60px" }}
                    >
                      {ctaLabel}
                      <FaArrowRight className="w-3 lg:w-4 h-3 lg:h-4" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
